import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Calendar, MapPin, Building, Award } from 'lucide-react';

const Experience: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const experiences = [
    {
      title: 'Software Developer',
      company: 'SISL Infotech',
      location: 'India',
      period: 'May 2025 - Present',
      type: 'Full-time',
      description: 'Leading modernization of government systems and delivering national-level projects with focus on security and compliance.',
      achievements: [
        'Led modernization of the State Land Records System, implementing real-time property ownership, dispute tracking, and digital workflows',
        'Designed and deployed secure APIs with Node.js, Express, MySQL, and RBAC, ensuring compliance with government security protocols',
        'Delivered national-level projects including Har Ghar Tiranga website for NIC Jammu (Independence Day 2025)',
        'Initiated secure Defence Portal with encrypted workflows and SSL-secured infrastructure using React, Node.js, MySQL'
      ],
      technologies: ['React', 'Node.js', 'Express', 'MySQL', 'RBAC', 'SSL', 'Government Security Protocols']
    },
    {
      title: 'Software Developer',
      company: 'Krishrad Innovations',
      location: 'India',
      period: 'Jun 2023 - May 2025',
      type: 'Full-time',
      description: 'Spearheaded development of educational and travel platforms with focus on real-time features and optimization.',
      achievements: [
        'Spearheaded development of student dashboards and job portals for College Dwarka, including placement tracking and live announcements',
        'Built real-time chat system using Socket.io, Node.js, MongoDB for enhanced user interaction',
        'Built, optimized, and deployed full-stack applications with React, Node.js, PHP, and REST APIs',
        'Improved SEO rankings and reduced load times by 30% through caching and frontend optimization',
        'Delivered client platforms including TravelHub, ZoraTravels, GM Virk, and College Dwarka'
      ],
      technologies: ['React', 'Node.js', 'PHP', 'MongoDB', 'Socket.io', 'REST APIs', 'SEO Optimization']
    },
    {
      title: 'Web Developer',
      company: 'Aimerse Technologies',
      location: 'India',
      period: 'Jan 2023 - May 2023',
      type: 'Full-time',
      description: 'Developed responsive UI components for e-learning platforms with focus on user experience and cross-browser compatibility.',
      achievements: [
        'Developed responsive UI components for e-learning platforms using React, Tailwind, and Bootstrap',
        'Ensured cross-browser compatibility and improved user engagement',
        'Collaborated with backend teams to integrate REST APIs and perform QA testing',
        'Optimized user workflows for smoother learner experiences'
      ],
      technologies: ['React', 'Tailwind CSS', 'Bootstrap', 'REST APIs', 'QA Testing', 'Cross-browser Compatibility']
    }
  ];

  const education = [
    {
      degree: 'MBA (Marketing)',
      school: 'IGNOU',
      period: '2021 - 2023',
      description: 'Specialized in marketing strategies and business management.',
      achievements: ['Marketing Focus']
    },
    {
      degree: 'Bachelor of Engineering (Electronics & Communication)',
      school: 'MIET',
      period: '2014 - 2018',
      description: 'Foundation in electronics and communication engineering.',
      achievements: ['Electronics & Communication Engineering']
    }
  ];

  const certifications = [
    {
      name: '3rd Place - Poster Presentation',
      issuer: 'ABYANTRIX',
      date: '2023',
      credential: 'Big Data for Big Impact'
    },
    {
      name: '9th Position - Jammu District',
      issuer: '8th Board Examinations',
      date: 'Academic Excellence',
      credential: 'Top Student Ranking'
    }
  ];

  return (
    <section id="experience" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            <span className="gradient-text">Experience</span> & Education
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            My professional journey and educational background that shaped my expertise
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-600 to-purple-600 mx-auto rounded-full mt-6"></div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Work Experience */}
          <div className="lg:col-span-2">
            <motion.h3
              initial={{ opacity: 0, x: -30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-2xl font-bold text-gray-900 mb-8 flex items-center"
            >
              <Building className="w-6 h-6 mr-3 text-primary-600" />
              Work Experience
            </motion.h3>

            <div className="space-y-8">
              {experiences.map((exp, index) => (
                <motion.div
                  key={`${exp.company}-${exp.period}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                  className="relative"
                >
                  {/* Timeline line */}
                  {index < experiences.length - 1 && (
                    <div className="absolute left-6 top-16 bottom-0 w-0.5 bg-gray-200"></div>
                  )}
                  
                  <div className="flex items-start space-x-4">
                    {/* Timeline dot */}
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center shadow-lg">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    
                    <div className="flex-1 bg-gray-50 rounded-xl p-6 card-hover">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                        <h4 className="text-xl font-bold text-gray-900">{exp.title}</h4>
                        <span className="text-sm bg-primary-100 text-primary-800 px-3 py-1 rounded-full font-medium">
                          {exp.type}
                        </span>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row sm:items-center text-gray-600 mb-3 space-y-1 sm:space-y-0 sm:space-x-4">
                        <div className="flex items-center">
                          <Building className="w-4 h-4 mr-2" />
                          {exp.company}
                        </div>
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-2" />
                          {exp.location}
                        </div>
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-2" />
                          {exp.period}
                        </div>
                      </div>
                      
                      <p className="text-gray-600 mb-4">{exp.description}</p>
                      
                      <div className="mb-4">
                        <h5 className="font-semibold text-gray-900 mb-2">Key Achievements:</h5>
                        <ul className="space-y-1">
                          {exp.achievements.map((achievement, idx) => (
                            <li key={idx} className="flex items-start">
                              <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                              <span className="text-sm text-gray-600">{achievement}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        {exp.technologies.map((tech) => (
                          <span key={tech} className="skill-badge text-xs">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Education & Certifications */}
          <div className="space-y-8">
            {/* Education */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Award className="w-6 h-6 mr-3 text-primary-600" />
                Education
              </h3>
              
              {education.map((edu, index) => (
                <motion.div
                  key={edu.degree}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                  className="bg-gray-50 rounded-xl p-6 card-hover mb-6"
                >
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{edu.degree}</h4>
                  <p className="text-primary-600 font-medium mb-2">{edu.school}</p>
                  <p className="text-gray-600 text-sm mb-3">{edu.period}</p>
                  <p className="text-gray-600 text-sm mb-3">{edu.description}</p>
                  <div className="space-y-1">
                    {edu.achievements.map((achievement, idx) => (
                      <div key={idx} className="flex items-center text-sm text-gray-600">
                        <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mr-2"></div>
                        {achievement}
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Certifications */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Award className="w-6 h-6 mr-3 text-primary-600" />
                Certifications
              </h3>
              
              <div className="space-y-4">
                {certifications.map((cert, index) => (
                  <motion.div
                    key={cert.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.7 + index * 0.1 }}
                    className="bg-white border border-gray-200 rounded-xl p-4 card-hover"
                  >
                    <h4 className="font-semibold text-gray-900 mb-1">{cert.name}</h4>
                    <p className="text-primary-600 text-sm mb-1">{cert.issuer}</p>
                    <div className="flex justify-between items-center text-sm text-gray-600">
                      <span>{cert.date}</span>
                      <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                        {cert.credential}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience; 